import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react"

import { 
  ColumnSelector, 
  DownloadButton, 
  FilterSelector,
  LoadingPlaceholder, 
  PaginatorInfo, 
  RefreshButton, 
  SearchInput 
} from "@components"

import { CircleStackIcon, Square3Stack3DIcon } from "@heroicons/react/24/outline"

import PropTypes from "prop-types"

const DataTable = ({
  actions = null,
  columns = null,
  currentPage,
  currentSortOrder,
  data,
  disabledKeys = [],
  disallowEmptyFilterSelection = true,
  downloadOptions = null,
  filters = null,
  filtersSelectionMode = "multiple",
  handleDownload = () => {},
  handleRenderCell,
  handleRefetch = () => {},
  handlePageChange = () => {},
  handleSearchChange,
  handleSearchClear,
  handleSelectionChange = () => {},
  handleSortChange = () => {},
  handleToggleColumn = () => {},
  handleToggleFilter,
  handleToggleSessionFilter,
  header = null,
  isDownloading = false,
  isRefetching = false,
  loading,
  paginatorInfo,
  noDataMessage = null,
  rowClasses = {},
  searchInputPlaceHolder = "Search by term",
  searchInputRef = null,
  searchTerm,
  selectedColumns,
  selectedFilters = null,
  selectedKeys = new Set([]),
  selectedSessions = null,
  selectionMode = "none",
  sessionFilters,
  showDownloadButton = false,
  showColumnsButton = true,
  showFilterCount = false,
  showPagination = true,
  showRefetchButton = false,
  showSearch = true,
  tableClasses = "",
}) => (
  <Card fullWidth className="shadow-lg">
    {header && (
      <CardHeader className="text-white bg-indigo-800">
        {header}
      </CardHeader>
    )}
    
    <CardHeader className="flex items-center gap-2">
      {showSearch && (
        <SearchInput
          debounceMilliSeconds={300}
          placeholder={searchInputPlaceHolder}
          handleChange={handleSearchChange}
          handleClear={handleSearchClear}
          innerRef={searchInputRef}
          value={searchTerm}
        />
      )}

      {showColumnsButton && (
        <ColumnSelector
          columns={columns}
          selectedColumns={selectedColumns}
          toggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
        />
      )}

      {filters && (
        <FilterSelector
          closeOnSelect={filtersSelectionMode === "single" ? true : false}
          disallowEmptySelection={disallowEmptyFilterSelection}
          filters={filters}
          selectionMode={filtersSelectionMode}
          selectedFilters={selectedFilters}
          showFilterCount={showFilterCount}
          toggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
        />
      )}

      {sessionFilters && Object.keys(sessionFilters).length > 1 && (
        <FilterSelector
          disallowEmptySelection={false}
          icon={<Square3Stack3DIcon className="w-auto h-6" />}
          filters={sessionFilters}
          selectionMode="single"
          selectedFilters={selectedSessions}
          showFilterCount={true}
          toggleFilter={selectedSession => handleToggleSessionFilter(selectedSession)}
        />
      )}

      {showDownloadButton && (
        <DownloadButton
          handleClick={type => handleDownload(type)}
          isLoading={isDownloading}
          options={downloadOptions}
        />
      )}

      {showRefetchButton && <RefreshButton handleClick={() => handleRefetch()} isLoading={isRefetching} />}

      {actions && actions}
    </CardHeader>

    <Divider />
    
    <CardBody>
      {data?.length > 0 && !loading && (
        <Table
          aria-label="Data Table"
          classNames={{
            table: `${tableClasses} mb-2`,
            wrapper: "rounded shadow-none p-0",
          }}
          disabledKeys={disabledKeys}
          isStriped
          selectionMode={selectionMode}
          selectedKeys={selectedKeys}
          sortDescriptor={currentSortOrder}
          onSelectionChange={selectedItems => handleSelectionChange(selectedItems)}
          onSortChange={sortDescriptor => handleSortChange(sortDescriptor)}
          isCompact
          layout="auto"
          fullWidth
          variants={["outline-none"]}
        >
          <TableHeader>
            {selectedColumns && 
              selectedColumns.map(key => {
                const column = columns[key]
                const alignment = column?.align || "text-start"
                const classes = column?.classes ? `${column.classes} ${alignment}` : `${alignment}`

                return (
                  <TableColumn key={column.id} allowsSorting={column?.sortable || false} className={classes}>
                    {column.name}
                  </TableColumn>
                )
              })}
          </TableHeader>

          <TableBody>
            {data && data.length > 0 && !loading &&
              data.map(item => (
                <TableRow key={item.id} className={rowClasses[item.id] || ""}>
                  {selectedColumns.map(key => {
                    const column = columns[key]                  
                    const alignment = column?.align || "start"
                    const classes = column?.classes ? `${column.classes} ${alignment}` : `${alignment}`

                    return (
                      <TableCell key={key} className={`${classes} whitespace-nowrap`}>
                        {handleRenderCell(item, key)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      )}
      
      {loading && <LoadingPlaceholder classNames="mt-2" caption="Please wait. Loading data..." icon={CircleStackIcon} />}
      {!loading && data?.length === 0 && noDataMessage}
    </CardBody>

    
    {showPagination && paginatorInfo?.lastPage > 1 && (  
      <div>
        <Divider />
        
        <CardFooter>
          <div className="flex items-center justify-between w-full">
            <PaginatorInfo paginatorInfo={paginatorInfo}></PaginatorInfo>

            <Pagination
              isCompact
              showControls
              showShadow
              color="secondary"
              page={currentPage}
              total={paginatorInfo?.lastPage}
              onChange={page => handlePageChange(page)}
            />
          </div>
        </CardFooter>
      </div>
    )}
  </Card>
)

DataTable.propTypes = {
  actions: PropTypes.node,
  columns: PropTypes.object,
  currentPage: PropTypes.number,
  currentSortOrder: PropTypes.object,
  data: PropTypes.array,
  disallowEmptyFilterSelection: PropTypes.bool,
  disabledKeys: PropTypes.array,
  downloadOptions: PropTypes.array,
  filters: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  filtersSelectionMode: PropTypes.string,
  handleDownload: PropTypes.func,
  handleRenderCell: PropTypes.func.isRequired,
  handleRefetch: PropTypes.func,
  handlePageChange: PropTypes.func,
  handleSearchChange: PropTypes.func.isRequired,
  handleSearchClear: PropTypes.func.isRequired,
  handleSelectionChange: PropTypes.func,
  handleSortChange: PropTypes.func,
  handleToggleColumn: PropTypes.func,
  handleToggleFilter: PropTypes.func,
  handleToggleSessionFilter: PropTypes.func,
  header: PropTypes.node,
  isDownloading: PropTypes.bool,
  isRefetching: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  paginatorInfo: PropTypes.object,
  noDataMessage: PropTypes.node,
  rowClasses: PropTypes.object,
  searchInputPlaceHolder: PropTypes.string,
  searchInputRef: PropTypes.object,
  searchTerm: PropTypes.string,
  selectedColumns: PropTypes.array,
  selectedFilters: PropTypes.array,
  selectionMode: PropTypes.string,
  selectedSessions: PropTypes.array,
  sessionFilters: PropTypes.object,
  showDownloadButton: PropTypes.bool,
  showFilterCount: PropTypes.bool,
  showPagination: PropTypes.bool,
  showRefetchButton: PropTypes.bool,
  showSearch: PropTypes.bool,
  tableClasses: PropTypes.string,
}

export default DataTable
